import * as Api from '@ViewModels';
import { Checkbox } from '@HoudiniUI/Checkbox';
import { Field, FieldContent, FieldDescription, FieldGroup, FieldLabel } from '@HoudiniUI/Field';
import { Input } from '@HoudiniUI/Input';
import { css } from 'aphrodite';
import { useId, useState } from 'react';
import { AIPromptUsage, IAIPromptTemplate, createAIPromptContent } from '../../../../models/AIPromptTemplate';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useSaveAIPrompt } from '../../../../queries/AIPrompt';
import { Button } from '@HoudiniUI/Button';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '@HoudiniUI/Dialog';
import { styleSheet } from './styles';

interface IProps {
	description: string;
	impersonationContext?: Api.IImpersonationContext;
	isOpen: boolean;
	namePlaceholder: string;
	/** Under the saved text: what the save leaves behind, for a form whose other settings stay per use. */
	note?: string;
	onOpenChange: (open: boolean) => void;
	/** The prompt as saved, so the field can credit it: its text is now that prompt's words. */
	onSaved?: (prompt: IAIPromptTemplate) => void;
	text: string;
	usage: AIPromptUsage;
}

export const SavePromptDialog: React.FC<IProps> = ({
	description,
	impersonationContext,
	isOpen,
	namePlaceholder,
	note,
	onOpenChange,
	onSaved,
	text,
	usage,
}) => {
	const id = useId();
	const errorMessages = useErrorMessages();
	const [name, setName] = useState('');
	const [shareWithTeam, setShareWithTeam] = useState(false);

	const savePrompt = useSaveAIPrompt({
		impersonationContext,
		onError: error => errorMessages.pushApiError(error),
		onSuccess: saved => {
			onOpenChange(false);
			setName('');
			setShareWithTeam(false);
			onSaved?.(saved);
		},
	});

	const onSave = () => {
		savePrompt.mutate({
			content: createAIPromptContent(text),
			name: name.trim(),
			// Scope is the only thing that decides who else sees it; the server refuses Industry either way.
			scope: shareWithTeam ? Api.TemplateScope.Account : Api.TemplateScope.User,
			usage,
		});
	};

	return (
		<Dialog dismissOnOutsideClick={false} onOpenChange={onOpenChange} open={isOpen}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Save as a prompt</DialogTitle>
					<DialogDescription>{description}</DialogDescription>
				</DialogHeader>
				<DialogBody>
					<FieldGroup>
						<div className={css(styleSheet.savedPrompt)}>
							<span className={css(styleSheet.savedPromptLabel)}>What gets saved</span>
							<p className={css(styleSheet.savedPromptText)}>{text}</p>
							{note ? <span className={css(styleSheet.savedPromptNote)}>{note}</span> : null}
						</div>

						<Field>
							<FieldLabel htmlFor={`${id}-name`}>Name</FieldLabel>
							<Input
								id={`${id}-name`}
								onChange={event => setName(event.target.value)}
								placeholder={namePlaceholder}
								value={name}
							/>
							<FieldDescription>What you will look for under My prompts next time.</FieldDescription>
						</Field>

						<Field orientation='horizontal'>
							<Checkbox
								checked={shareWithTeam}
								id={`${id}-share`}
								onCheckedChange={checked => setShareWithTeam(checked === true)}
							/>
							<FieldContent>
								<FieldLabel htmlFor={`${id}-share`}>Share with the team</FieldLabel>
								<FieldDescription>
									Everyone on the account can start from it. Leave this off and it stays yours.
								</FieldDescription>
							</FieldContent>
						</Field>
					</FieldGroup>
				</DialogBody>
				<DialogFooter>
					<Button disabled={savePrompt.isPending} onClick={() => onOpenChange(false)} variant='outline'>
						Cancel
					</Button>
					<Button disabled={!name.trim() || savePrompt.isPending} isLoading={savePrompt.isPending} onClick={onSave}>
						Save prompt
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
